import type {
  AffiliateFilleulView,
  AffiliateLoginResponse,
  AffiliateProgramPublicView,
  AffiliateRecruitView,
  AffiliateRecord,
  AffiliateRegistrationRequest,
  AffiliateStatsView,
  AffiliateToolkitProduct,
} from "@itaas/pep-contracts";
import { PEP_API_BASE } from "../config";
import {
  affiliateAuthHeaders,
  affiliateAuthHeadersOnly,
  clearAffiliateSessionToken,
  saveAffiliateSessionToken,
} from "../utils/affiliate-session";

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PEP_API_BASE}${path}`, init);
  if (!res.ok) {
    let message = `API ${path}: ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body.message) {
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
      }
    } catch {
      try {
        const text = await res.text();
        if (text) message = text;
      } catch {
        // ignore
      }
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export function getAffiliatePrograms() {
  return fetchJson<{
    programs: AffiliateProgramPublicView[];
    emailVerificationRequired: boolean;
    phoneVerificationRequired: boolean;
  }>("/affiliate/programs");
}

export function getAffiliateToolkit() {
  return fetchJson<{ products: AffiliateToolkitProduct[] }>("/affiliate/toolkit");
}

export function checkAffiliateEmailAvailable(email: string) {
  return fetchJson<{ available: boolean; reason?: "invalid" }>(
    `/affiliate/email/available?email=${encodeURIComponent(email.trim())}`,
  );
}

export function sendAffiliateEmailCode(email: string, name?: string) {
  return fetchJson<{ sent: boolean; devCode?: string; expiresInSec: number }>(
    "/affiliate/email/send-code",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, name }),
    },
  );
}

export function verifyAffiliateEmail(email: string, code: string) {
  return fetchJson<{ emailVerificationToken: string; expiresInSec: number }>(
    "/affiliate/email/verify",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    },
  );
}

/** @deprecated Utiliser sendAffiliateEmailCode */
export function sendAffiliatePhoneCode(phone: string) {
  return fetchJson<{ sent: boolean; devCode?: string; expiresInSec: number }>(
    "/affiliate/phone/send-code",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone }),
    },
  );
}

export function verifyAffiliatePhone(phone: string, code: string) {
  return fetchJson<{ phoneVerificationToken: string; expiresInSec: number }>(
    "/affiliate/phone/verify",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, code }),
    },
  );
}

export function sendAffiliateLoginCode(login: string) {
  return fetchJson<{
    sent: boolean;
    devCode?: string;
    expiresInSec: number;
    loginHint?: string;
    affiliateCode?: string;
  }>("/affiliate/auth/send-code", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ login: login.trim() }),
  });
}

export function verifyAffiliateLogin(login: string, code: string) {
  return fetchJson<AffiliateLoginResponse>("/affiliate/auth/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ login: login.trim(), code }),
  });
}

export function getAffiliateMe() {
  return fetchJson<AffiliateStatsView>("/affiliate/auth/me", {
    headers: affiliateAuthHeadersOnly(),
  });
}

export function getAffiliateReferrals(limit = 50) {
  return fetchJson<{ items: AffiliateFilleulView[] }>(
    `/affiliate/referrals?limit=${limit}`,
    { headers: affiliateAuthHeadersOnly() },
  );
}

export function getAffiliateRecruits(limit = 50) {
  return fetchJson<{ items: AffiliateRecruitView[] }>(
    `/affiliate/recruits?limit=${limit}`,
    { headers: affiliateAuthHeadersOnly() },
  );
}

export function logoutAffiliate() {
  return fetchJson<{ ok: boolean }>("/affiliate/auth/logout", {
    method: "POST",
    headers: affiliateAuthHeaders(),
  }).finally(() => clearAffiliateSessionToken());
}

export function registerAffiliate(payload: AffiliateRegistrationRequest) {
  return fetchJson<{
    id: string;
    code: string;
    slug: string;
    products: string[];
    createdAt: string;
    accessToken: string;
  }>("/affiliate/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getAffiliateStats(code: string) {
  return fetchJson<AffiliateStatsView>(`/affiliate/stats/${encodeURIComponent(code)}`, {
    headers: affiliateAuthHeadersOnly(),
  });
}

export function addAffiliateProducts(products: string[]) {
  return fetchJson<{ code: string; products: string[] }>("/affiliate/add-products", {
    method: "POST",
    headers: affiliateAuthHeaders(),
    body: JSON.stringify({ products }),
  });
}

export function validateAffiliateCode(code: string) {
  return fetchJson<{
    code: string;
    name: string;
    products: string[];
    profileType: string;
    organization?: string;
  }>(`/affiliate/validate/${encodeURIComponent(code)}`);
}

export function fetchAffiliatesAdmin() {
  const key = sessionStorage.getItem("pep-admin-key");
  const token = sessionStorage.getItem("pep-iam-token");
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (key) headers["x-pep-admin-key"] = key;

  return fetchJson<AffiliateRecord[]>("/affiliate", { headers });
}

function adminHeaders(): Record<string, string> {
  const key = sessionStorage.getItem("pep-admin-key");
  const token = sessionStorage.getItem("pep-iam-token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (key) headers["x-pep-admin-key"] = key;
  return headers;
}

export function updateAffiliateStatus(
  id: string,
  status: "active" | "suspended",
  suspensionReason?: string,
) {
  return fetchJson<AffiliateRecord>(`/affiliate/${id}/status`, {
    method: "PATCH",
    headers: adminHeaders(),
    body: JSON.stringify({ status, suspensionReason }),
  });
}

export function recordAffiliatePayout(id: string, note?: string) {
  return fetchJson<AffiliateRecord>(`/affiliate/${id}/record-payout`, {
    method: "POST",
    headers: adminHeaders(),
    body: JSON.stringify({ note }),
  });
}

export { saveAffiliateSessionToken };
